import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover, TextField, Grid, InputAdornment, Button, Typography, useTheme } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { convertDateToUTC, intlFormatDateShort, isValidDate } from "../../../../../utils/time-utils";
import { AbsencesPeriodOptionValue } from "../interfaces";

interface Props {
  period: AbsencesPeriodOptionValue;
  handlePeriodChange: (period: AbsencesPeriodOptionValue) => void;
}

export const CustomDateRangePicker = ({ period, handlePeriodChange }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.quickfilters.absences_period";

  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState(null);

  const [fromDate, setFromDate] = useState<Date | null>(period.fromDate);
  const [toDate, setToDate] = useState<Date | null>(period.toDate);

  const currentValue =
    period.fromDate && period.toDate
      ? `${intlFormatDateShort(period.fromDate)}-${intlFormatDateShort(period.toDate)}`
      : !!period.fromDate && !period.toDate
      ? `From: ${intlFormatDateShort(period.fromDate)}`
      : !!period.toDate && !period.fromDate
      ? `To: ${intlFormatDateShort(period.toDate)}`
      : "Not selected";

  const handleClick = (event: any) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const open = Boolean(anchorEl);

  const confirmHandler = () => {
    setAnchorEl(null);

    (!!fromDate || !!toDate) &&
      handlePeriodChange({
        fromDate: fromDate ? convertDateToUTC(fromDate) : null,
        toDate: toDate ? convertDateToUTC(toDate) : null,
      });
  };

  return (
    <>
      <Grid onClick={handleClick}>
        <TextField
          title={"Select period"}
          value={currentValue}
          label="Period"
          sx={{
            width: "100%",
            caretColor: "transparent",
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                title={"Select period"}
              >
                <FontAwesomeIcon icon={faCalendarAlt} />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Grid
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "10px",
            color: theme.palette.background.paper,
          }}
        >
          <Grid sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px" }}>
            <DesktopDatePicker
              inputFormat="dd.MM.yyyy"
              mask="__.__.____"
              label={t(`${tRP}.from_date`)}
              value={fromDate}
              maxDate={toDate ? toDate : undefined}
              onChange={(newValue) => {
                if (isValidDate(newValue)) {
                  setFromDate(newValue);
                }
              }}
              renderInput={(params) => <TextField {...params} />}
            />

            <Typography sx={{ color: theme.palette.text.primary }}>-</Typography>

            <DesktopDatePicker
              inputFormat="dd.MM.yyyy"
              mask="__.__.____"
              label={t(`${tRP}.to_date`)}
              value={toDate}
              minDate={fromDate ? fromDate : undefined}
              onChange={(newValue) => {
                setToDate(newValue);
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </Grid>

          <Grid sx={{ display: "flex", flexDirection: "row-reverse" }}>
            <Button
              variant="contained"
              onClick={confirmHandler}
            >
              Confirm
            </Button>
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};
